/**
 * Protocolo de red del netcode autoritativo (M1).
 *
 * Transport-agnóstico y SIN DOM: las mismas estructuras viajan por el loopback
 * in-process (tests, dev) y por el WebSocket real de Colyseus (M1 infra). El
 * cliente solo envía inputs; el servidor devuelve snapshots autoritativos.
 */
import { GameState, InputState } from "../game";

/** Fases del ciclo de vida de una sala (controladas por el admin). Vive aquí, y
 *  no en `wire.ts`, porque el snapshot la lleva y `wire` ya depende de este
 *  módulo (al revés sería un ciclo). `wire.ts` la reexporta. */
export type RoomPhase = "lobby" | "countdown" | "playing" | "paused" | "over";

export const NET_TICK_RATE = 60;
export const NET_FIXED_DT = 1 / NET_TICK_RATE;

/**
 * Snapshots por segundo. 30 Hz sobre una simulación de 60.
 *
 * Subido desde 20 Hz por una razón medible: el rival y el balón solo pueden
 * corregirse cuando llega un snapshot, así que el intervalo es un suelo de
 * frescura — a 20 Hz, la información del rival tenía hasta 50 ms de antigüedad
 * ANTES de contar la latencia; a 30 Hz, 33. Ese tramo es el que separa un
 * disputa de balón que se siente justa de una en la que "llegué yo primero" y
 * el servidor opina otra cosa.
 *
 * El precio es ancho de banda: un snapshot ronda los 250 B en JSON, así que
 * pasar de 20 a 30 Hz son ~2,5 KB/s más por jugador. A cambio, el jitter-buffer
 * del cliente puede rendirse antes (menos delay de interpolación) y el estimador
 * de RTT descuenta menos muestreo.
 */
export const SNAPSHOT_RATE = 30;
export const SNAPSHOT_INTERVAL_TICKS = Math.round(NET_TICK_RATE / SNAPSHOT_RATE); // 2

/**
 * Cuántos ticks puede adelantarse un cliente (anti-speedhack: el cliente no
 * dicta el reloj del servidor).
 *
 * Este número fija la LATENCIA MÁXIMA JUGABLE, y la cuenta no es evidente: el
 * cliente necesita liderar al servidor **2×** la latencia de ida, no una. Los
 * snapshots que recibe ya vienen con un retardo de ida (le dicen dónde estaba el
 * servidor, no dónde está), y sus inputs tardan otro tanto en llegar. Si su
 * reloj no cubre las dos mitades, sus inputs aterrizan con `tick < server.tick`,
 * el servidor los descarta por tardíos y el jugador **no se mueve** en la
 * simulación autoritativa — aunque su predicción local se vea perfectamente
 * fluida y el ping marque 0.
 *
 * Con el valor anterior (24 ⇒ lead útil 20) el techo caía en ~340 ms de RTT:
 * suficiente para jugar dentro de una región, pero cualquier conexión móvil
 * mala o una partida intercontinental quedaba muerta de golpe y sin diagnóstico.
 * A 45 ticks (750 ms de ventana, lead útil 41) el techo sube a ~680 ms de RTT.
 *
 * Subirlo NO debilita el anti-speedhack: la garantía dura la da `step()`, que
 * avanza exactamente un tick pase lo que pase, así que adelantarse solo hace que
 * tus propios inputs se apliquen más tarde — te perjudica a ti. Y la memoria por
 * cliente sigue acotada por `MAX_QUEUED_INPUTS`.
 */
export const MAX_INPUT_LEAD = 45;

/**
 * Ventana del servidor ANTERIOR a la ampliación. Se conserva porque los tests
 * de convivencia entre versiones la necesitan para emular ese servidor: un
 * cliente nuevo tiene que seguir funcionando contra un backend sin actualizar.
 */
export const LEGACY_MAX_INPUT_LEAD = 24;

/** Estado mínimo de un cuerpo para viajar por la red (posición + velocidad). */
export interface BodyState {
  x: number;
  y: number;
  vx: number;
  vy: number;
}

/** Comando de input etiquetado con el tick que el cliente simuló. */
export interface InputCommand {
  tick: number;
  input: InputState;
}

/** Snapshot autoritativo que el servidor difunde al cliente. */
export interface Snapshot {
  /** Tick del servidor tras aplicar la simulación de este snapshot. */
  serverTick: number;
  /** Último tick de input del cliente que el servidor procesó (para reconciliar). */
  ackTick: number;
  /** Cuerpos autoritativos de los jugadores, por slot (índice 0 y 1). */
  players: BodyState[];
  /**
   * Qué slots tienen a alguien dentro (por slot).
   *
   * Sin esto, el cliente de una sala de 8 con 2 personas dibujaba (y simulaba)
   * seis discos parados: no eran bots, eran sitios vacíos. Viaja en el snapshot
   * y no en `state` porque cambia con cada entrada/salida y el cliente que se
   * pierda un mensaje no puede quedarse con seis fantasmas en la cancha.
   * Opcional: los snapshots hechos a mano en tests no lo llevan (todo activo).
   */
  active?: boolean[];
  ball: BodyState;
  score: [number, number];
  /** Fin de partida autoritativo (opcional; el servidor lo incluye). */
  over?: boolean;
  winner?: 0 | 1 | null;
  /**
   * Reloj autoritativo del partido (segundos transcurridos). DEBE viajar en el
   * snapshot: sin él, la reconciliación re-cuenta el tiempo de los inputs
   * pendientes en cada snapshot y el reloj corre de más. Opcional para no romper
   * snapshots construidos a mano en tests; el servidor siempre lo incluye.
   */
  matchTime?: number;
  /** Pausa de saque autoritativa (segundos); el reloj no avanza mientras >0. */
  resetTimer?: number;
  /** Prórroga a gol de oro en curso (GP-1). El cliente la necesita para no
   *  dibujar un reloj que ya pasó del límite como si quedara tiempo. */
  overtime?: boolean;
  /**
   * Equipo con derecho al saque (o null). Viaja porque el cliente lo NECESITA
   * para dos cosas: dibujar el círculo encendido del color de quien saca, y
   * predecir la misma restricción que el servidor — si no, el disco del que no
   * puede entrar se movería en local y el servidor lo devolvería fuera, que es
   * el peor tipo de corrección: la que castiga por hacer algo que el juego te
   * dejaba hacer.
   */
  kickoff?: 0 | 1 | null;
  /**
   * Carga acumulada de cada slot, en segundos (ver `POSSESSION_TIME_MAX`).
   *
   * Tiene que viajar. El cliente la calculaba solo, y al reconciliar rebobina
   * los CUERPOS pero no la carga: cada snapshot volvía a re-simular los inputs
   * pendientes y volvía a sumar posesión sobre la que ya tenía. Medido, con
   * 100 ms de latencia el aro de carga se llenaba SIETE veces más rápido de lo
   * que el servidor contaba — así que el disparo salía flojo justo cuando el
   * dibujo decía "a tope". Es información de juego, no decorado.
   *
   * De paso arregla otra cosa: la carga del RIVAL, que el cliente estimaba por
   * su cuenta y ahora es la de verdad. Ver venir un disparo cargado es parte de
   * jugar contra alguien.
   */
  possession?: number[];
  /**
   * Animación de gol en curso (1 = acaba de entrar, 0 = terminada) y a quién.
   *
   * Viaja por el mismo motivo que `possession`: se consumía en el REPLAY. Cada
   * reconciliación re-simula los inputs pendientes y cada uno volvía a restarle
   * tiempo, así que una celebración de un segundo se apagaba en una décima —y
   * cuanto más se tecleaba, más pendientes había y antes se apagaba. Parecía que
   * el propio teclado interrumpía el festejo, y en cierto modo era verdad.
   */
  goalAnim?: number;
  goalSide?: 0 | 1 | null;
  /** Segundos que faltan para el saque durante la fase `countdown` (GP-2).
   *  Viaja con la fase para que los dos jugadores vean el MISMO número: si cada
   *  cliente lo contase por su cuenta, empezarían a jugar en instantes distintos. */
  countdown?: number;
  /**
   * Fase de la sala (lobby/jugando/pausa/fin). Viaja en CADA snapshot por la
   * misma razón que el reloj: es estado autoritativo, y el mensaje `state` que
   * la traía se manda una sola vez al entrar y en los cambios. Un cliente que se
   * perdiera ese único mensaje creía "jugando" para siempre (su valor por
   * defecto) aunque la sala estuviera parada — y entonces enseñaba el HUD de
   * partida con el reloj congelado, el ping en "— ms" y el aviso de "sincronizando
   * con el servidor", que es mentira: el socket está perfecto, lo que pasa es que
   * la sala no está jugando. Repetida a 20 Hz, la discrepancia se cura sola.
   */
  phase?: RoomPhase;
  /**
   * Cuántos ticks por DELANTE del reloj del servidor está el input más nuevo que
   * recibió de este slot (`maxInputTick - serverTick`). Es la señal de control del
   * jitter-buffer: el cliente ajusta su lead para mantenerla en un objetivo
   * pequeño y positivo. Negativo = los inputs llegan tarde (el servidor se queda
   * sin inputs frescos) → el cliente debe adelantarse más. Opcional: los
   * snapshots hechos a mano en tests no lo llevan (el cliente cae al lead fijo).
   */
  bufAhead?: number;
}

export function readBody(g: GameState, idx: number): BodyState {
  const w = g.world;
  return { x: w.px[idx], y: w.py[idx], vx: w.vx[idx], vy: w.vy[idx] };
}

export function writeBody(g: GameState, idx: number, b: BodyState): void {
  const w = g.world;
  w.px[idx] = b.x;
  w.py[idx] = b.y;
  w.vx[idx] = b.vx;
  w.vy[idx] = b.vy;
}

export function cloneInput(i: InputState): InputState {
  return {
    up: i.up, down: i.down, left: i.left, right: i.right,
    kick: i.kick, kickRelease: i.kickRelease,
    // Los ejes analógicos viajan con el resto del input: son parte de la orden,
    // no un adorno del cliente (ver `InputState.mx`).
    mx: i.mx ?? 0, my: i.my ?? 0,
  };
}

export function lerpBody(a: BodyState, b: BodyState, t: number): BodyState {
  return {
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
    vx: a.vx + (b.vx - a.vx) * t,
    vy: a.vy + (b.vy - a.vy) * t,
  };
}
